import { Link } from "react-router-dom";
import type { Post } from "@/types/content";

const categoryLabels: Record<string, string> = {
  biaoqingbao: "表情包",
  duanzi: "段子",
  shenhuifu: "神回复",
  gengtu: "梗图",
  shipin: "二创视频",
};

function formatNum(value: number) {
  if (value >= 10000) return `${(value / 10000).toFixed(1)}w`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return String(value);
}

interface PostCardProps {
  post: Post;
  variant?: "grid" | "row" | "mini";
}

export default function PostCard({ post, variant = "grid" }: PostCardProps) {
  const label = categoryLabels[post.category] ?? "吃瓜";

  if (variant === "row") {
    return (
      <Link
        to={`/post/${post.id}`}
        className="group flex gap-3 md:gap-4 p-3 rounded-lg bg-background-50 border border-background-200/80 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="w-24 h-20 md:w-36 md:h-28 shrink-0 rounded-md overflow-hidden bg-background-100">
          <img
            src={post.cover}
            alt={`${post.title} - 网爆吃瓜${label}`}
            title={`${post.title} - 网爆吃瓜${label}`}
            className="w-full h-full object-top object-cover group-hover:scale-[1.04] transition-transform duration-500"
          />
        </div>
        <div className="min-w-0 flex flex-col justify-between py-0.5">
          <div>
            <h3 className="text-sm md:text-base font-medium text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
              {post.title}
            </h3>
            <p className="mt-1.5 text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2 hidden sm:block">
              {post.excerpt}
            </p>
          </div>
          <div className="mt-2 flex items-center gap-3 text-xs text-foreground-400">
            <span className="whitespace-nowrap">{post.author}</span>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-eye-line"></i>
            </span>
            <span>{formatNum(post.views)}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "mini") {
    return (
      <Link to={`/post/${post.id}`} className="group flex items-start gap-3 cursor-pointer">
        <span className="w-6 h-6 shrink-0 rounded-md bg-secondary-100 text-secondary-800 text-xs font-medium flex items-center justify-center mt-0.5">
          {post.tags[0]?.slice(0, 1) ?? "瓜"}
        </span>
        <div className="min-w-0">
          <h4 className="text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {post.title}
          </h4>
          <span className="mt-1 block text-xs text-foreground-400">{formatNum(post.views)} 次围观</span>
        </div>
      </Link>
    );
  }

  return (
    <article className="group h-full flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200/80 hover:border-primary-300 transition-colors cursor-pointer">
      <Link to={`/post/${post.id}`} className="flex flex-col h-full cursor-pointer">
        <div className="relative w-full aspect-[4/3] overflow-hidden bg-background-100">
          <img
            src={post.cover}
            alt={`${post.title} - 网爆吃瓜${label}`}
            title={`${post.title} - 网爆吃瓜${label}`}
            className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500"
          />
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs whitespace-nowrap">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-fire-line"></i>
            </span>
            {label}
          </span>
        </div>
        <div className="flex-1 flex flex-col p-4 md:p-5">
          <h3 className="text-base text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {post.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-500 leading-relaxed line-clamp-2 flex-1">{post.excerpt}</p>
          <div className="mt-4 flex items-center justify-between text-xs text-foreground-400">
            <span className="whitespace-nowrap">{post.author}</span>
            <span className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-eye-line"></i>
                </span>
                {formatNum(post.views)}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-heart-3-line"></i>
                </span>
                {formatNum(post.likes)}
              </span>
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}